import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Package, TrendingUp, TrendingDown, AlertTriangle, ShoppingCart, Calculator, Send, XCircle } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { CurrencyIcon } from '@/components/CurrencyIcon';
import { getCurrencySymbol } from '@/utils/currency';
import { customerOrderApi } from '@/services/customerOrderApi';
import { cashSettlementApi } from '@/services/cashSettlementApi';
import StatCard from '@/components/dashboard/StatCard';
import ServiceDashboard from '@/components/dashboard/ServiceDashboard';
import AllBranchesView from '@/components/admin/superadmin/AllBranchesView';
import BranchDetailedView from '@/components/admin/superadmin/BranchDetailedView';
import BranchServiceOverview from '@/components/admin/superadmin/BranchServiceOverview';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate?: string;
    endDate?: string;
    period?: string;
  }>({ period: 'month' });

  // Fetch pending settlement count for quick action
  const { data: pendingSettlements } = useQuery({
    queryKey: ['settlement-pending-count-dashboard'],
    queryFn: () => cashSettlementApi.getPendingCount(),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  // Fetch today's customer order stats by branch
  const { data: todayOrderStats } = useQuery({
    queryKey: ['todayOrderStatsByBranch'],
    queryFn: () => customerOrderApi.getTodayStatsByBranch(),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['superAdminDashboard', dateRange],
    queryFn: () => dashboardApi.getSuperAdminDashboard(dateRange),
    staleTime: 90 * 1000, // 90 seconds
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
    refetchOnWindowFocus: true,
  });

  const handleDateRangeChange = (newRange: typeof dateRange) => {
    setDateRange(newRange);
  };

  const handleBranchSelect = (branchId: string | null) => {
    setSelectedBranchId(branchId);
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const { summary, branches } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
        <p className="text-sm text-gray-600 mt-1">
          Company-wide analytics and branch performance
        </p>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Total Branches"
          value={summary.totalBranches}
          icon={Building2}
          iconColor="text-blue-600"
          iconBg="bg-blue-100"
        />
        <StatCard
          title="Total Customers"
          value={summary.totalCustomers}
          icon={Users}
          iconColor="text-green-600"
          iconBg="bg-green-100"
        />
        <StatCard
          title="Total Revenue"
          value={`${getCurrencySymbol()}${summary.totalRevenue.toLocaleString()}`}
          icon={CurrencyIcon}
          iconColor="text-cyan-600"
          iconBg="bg-cyan-100"
        />
        <StatCard
          title="Total Inventory"
          value={summary.totalInventoryItems}
          icon={Package}
          iconColor="text-purple-600"
          iconBg="bg-purple-100"
        />
        <StatCard
          title="Total Outstanding"
          value={`${getCurrencySymbol()}${(summary.totalOutstanding || 0).toLocaleString()}`}
          icon={AlertTriangle}
          iconColor="text-red-600"
          iconBg="bg-red-100"
        />
      </div>

      {/* Financial Overview */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {getCurrencySymbol()}{summary.totalRevenue.toLocaleString()}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-gray-600">Total Expenses</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {getCurrencySymbol()}{summary.totalExpenses.toLocaleString()}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CurrencyIcon className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-600">Total Profit</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {getCurrencySymbol()}{summary.totalProfit.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Cash Settlement Quick Actions */}
      {pendingSettlements && (pendingSettlements.submittedCount > 0 || pendingSettlements.rejectedCount > 0) && (
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-gray-900">Cash Settlement</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pendingSettlements.submittedCount > 0 && (
              <button
                onClick={() => navigate('/cash-settlements?tab=pending')}
                className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-left"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-900">{pendingSettlements.submittedCount}</p>
                  <p className="text-sm text-blue-700">Awaiting Approval</p>
                </div>
              </button>
            )}
            {pendingSettlements.rejectedCount > 0 && (
              <button
                onClick={() => navigate('/cash-settlements?tab=rejected')}
                className="flex items-center gap-3 p-4 bg-red-50 rounded-xl hover:bg-red-100 transition-colors text-left"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-900">{pendingSettlements.rejectedCount}</p>
                  <p className="text-sm text-red-700">Rejected</p>
                </div>
              </button>
            )}
            {pendingSettlements.pendingCount > 0 && (
              <button
                onClick={() => navigate('/cash-settlements?tab=all')}
                className="flex items-center gap-3 p-4 bg-yellow-50 rounded-xl hover:bg-yellow-100 transition-colors text-left"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-900">{pendingSettlements.pendingCount}</p>
                  <p className="text-sm text-yellow-700">Not Yet Submitted</p>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Service Dashboard - always visible */}
      <ServiceDashboard />

      {/* All Branches Performance */}
      {selectedBranchId ? (
        <BranchDetailedView
          branchId={selectedBranchId}
          dateRange={dateRange}
          onBack={() => setSelectedBranchId(null)}
        />
      ) : (
        <AllBranchesView
          branches={branches}
          onBranchSelect={handleBranchSelect}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
      )}

      {/* Today's Customer Order Requests */}
      {todayOrderStats && todayOrderStats.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-800">Today's Customer Order Requests</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Branch</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">New Orders</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Total Today</th>
                </tr>
              </thead>
              <tbody>
                {todayOrderStats.map((stat) => (
                  <tr key={stat.branchId} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 px-3 text-gray-900 font-medium">{stat.branchName}</td>
                    <td className="py-2 px-3 text-center">
                      {stat.todayNewCount > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 text-xs font-bold text-amber-700 bg-amber-100 rounded-full">
                          {stat.todayNewCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center text-gray-700">{stat.todayTotalCount}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className="py-2 px-3 text-gray-900">Total</td>
                  <td className="py-2 px-3 text-center text-amber-700">
                    {todayOrderStats.reduce((sum, s) => sum + s.todayNewCount, 0)}
                  </td>
                  <td className="py-2 px-3 text-center text-gray-700">
                    {todayOrderStats.reduce((sum, s) => sum + s.todayTotalCount, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Branch Service Overview */}
      <BranchServiceOverview />
    </div>
  );
}
